interface GaugeProps {
  label: string
  value: number
  max: number
  unit?: string
  color?: string
}

const ARC_RADIUS = 40
const STROKE_WIDTH = 8
const START_ANGLE = -135
const END_ANGLE = 135
const TOTAL_DEG = END_ANGLE - START_ANGLE

function polarToXY(cx: number, cy: number, r: number, angleDeg: number) {
  const rad = (angleDeg * Math.PI) / 180
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) }
}

function describeArc(cx: number, cy: number, r: number, startDeg: number, endDeg: number) {
  const start = polarToXY(cx, cy, r, startDeg)
  const end = polarToXY(cx, cy, r, endDeg)
  const largeArc = endDeg - startDeg > 180 ? 1 : 0
  return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArc} 1 ${end.x} ${end.y}`
}

function CapacityGauge({ label, value, max, unit = '', color = '#0070ab' }: GaugeProps) {
  const pct = max > 0 ? Math.min(value / max, 1) : 0
  const valueDeg = START_ANGLE + pct * TOTAL_DEG
  const cx = 50
  const cy = 50

  const statusColor =
    pct >= 0.9 ? '#dc2626' :
    pct >= 0.75 ? '#f59e0b' :
    color

  return (
    <div className="flex flex-col items-center">
      <div className="mm-gauge w-24 h-24">
        <svg viewBox="0 0 100 100" className="w-full h-full" style={{ transform: 'rotate(0deg)' }}>
          {/* Background arc */}
          <path
            d={describeArc(cx, cy, ARC_RADIUS, START_ANGLE, END_ANGLE)}
            fill="none"
            stroke="#e2e8f0"
            strokeWidth={STROKE_WIDTH}
            strokeLinecap="round"
          />
          {/* Value arc */}
          {pct > 0 && (
            <path
              d={describeArc(cx, cy, ARC_RADIUS, START_ANGLE, valueDeg)}
              fill="none"
              stroke={statusColor}
              strokeWidth={STROKE_WIDTH}
              strokeLinecap="round"
            />
          )}
          {/* Center text */}
          <text
            x={cx}
            y={cy - 2}
            textAnchor="middle"
            className="fill-ink-dark"
            style={{ fontSize: '14px', fontFamily: "'JetBrains Mono', monospace", fontWeight: 700 }}
          >
            {Math.round(pct * 100)}%
          </text>
          <text
            x={cx}
            y={cy + 12}
            textAnchor="middle"
            className="fill-ink-soft"
            style={{ fontSize: '8px', fontFamily: "'Work Sans', sans-serif" }}
          >
            {value.toLocaleString()}{unit ? ` ${unit}` : ''}
          </text>
        </svg>
      </div>
      <div className="text-xs font-medium text-ink text-center mt-1">{label}</div>
    </div>
  )
}

interface KioskRow {
  rank: number
  kiosk_group_name: string
  placement_type: string | null
  orders: number
  revenue: number
  new_band_orders: number
  reload_orders: number
}

interface CapacityGaugesProps {
  hourly?: Array<{ hour: number; orders: number; revenue: number; riders: number }>
  kiosks?: KioskRow[]
  metrics?: {
    revenue_total: number
    ride_gross: number
    guests: number
    riders_per_hour: number
    orders_per_hour: number
  }
  loading?: boolean
}

export function CapacityGauges({ hourly, kiosks, metrics, loading }: CapacityGaugesProps) {
  if (loading) {
    return (
      <div className="mm-card-v3 p-5">
        <div className="h-4 w-36 bg-slate-200 rounded animate-pulse mb-4" />
        <div className="flex justify-around">
          {[1, 2, 3].map((i) => (
            <div key={i} className="w-24 h-24 bg-slate-100 rounded-full animate-pulse" />
          ))}
        </div>
      </div>
    )
  }

  const peakRiders = hourly && hourly.length > 0
    ? Math.max(...hourly.map((h) => h.riders || 0))
    : 0
  const peakOrders = hourly && hourly.length > 0
    ? Math.max(...hourly.map((h) => h.orders || 0))
    : 0
  const currentRiders = metrics?.riders_per_hour ?? 0
  const currentOrders = metrics?.orders_per_hour ?? 0

  const totalKioskCapacity = kiosks ? kiosks.length * 150 : 0
  const totalKioskOrders = kiosks ? kiosks.reduce((s, k) => s + (k.orders || 0), 0) : 0

  const hasData = peakRiders > 0 || peakOrders > 0 || totalKioskCapacity > 0

  if (!hasData) {
    return (
      <div className="mm-card-v3 p-5">
        <h3 className="font-display font-semibold text-sm text-ink-dark mb-3">Capacity Utilization</h3>
        <div className="py-6 text-center text-sm text-ink-soft">
          Capacity data will appear once the event is live.
        </div>
      </div>
    )
  }

  return (
    <div className="mm-card-v3 p-5">
      <h3 className="font-display font-semibold text-sm text-ink-dark mb-4">Capacity Utilization</h3>
      <div className="flex justify-around flex-wrap gap-4">
        {peakRiders > 0 && (
          <CapacityGauge
            label="Riders/hr"
            value={currentRiders}
            max={Math.round(peakRiders * 1.1)}
            unit="/hr"
            color="#0070ab"
          />
        )}
        {peakOrders > 0 && (
          <CapacityGauge
            label="Orders/hr"
            value={currentOrders}
            max={Math.round(peakOrders * 1.1)}
            unit="/hr"
            color="#00c7ca"
          />
        )}
        {totalKioskCapacity > 0 && (
          <CapacityGauge
            label="Kiosk Load"
            value={totalKioskOrders}
            max={totalKioskCapacity}
            unit="orders"
            color="#005b92"
          />
        )}
      </div>
    </div>
  )
}
